import { UnreadIndicator } from "@circle-react-shared/UnreadIndicator";
import type { Chat } from "@circle-react/types/CommunityInbox/Chat";
import { Avatar } from "@circleco/compass/components/Avatar";
import { Icon } from "@circleco/compass/components/Icon";
import { Link } from "@circleco/compass/components/Link";
import { Tooltip } from "@circleco/compass/components/Tooltip";
import { Typography } from "@circleco/compass/components/Typography";
import type { MouseEvent } from "react";

import { t } from "@/i18n-js/instance";

import { useAvatarConversationPanelStore } from "../useAvatarConversationPanelStore";
import { buildAvatarPanelEntryUrl } from "./urlParams";

interface ChatListRowProps {
  chat: Chat;
}

const isPlainLeftClick = (event: MouseEvent) =>
  event.button === 0 &&
  !event.metaKey &&
  !event.ctrlKey &&
  !event.shiftKey &&
  !event.altKey;

export const ChatListRow = ({ chat }: ChatListRowProps) => {
  const selectAvatar = useAvatarConversationPanelStore(s => s.selectAvatar);
  const isFullscreen = useAvatarConversationPanelStore(
    s => s.panelState === "fullscreen",
  );
  const { name, avatar_url, id: avatarId } = chat.community_bot_profile;
  const aiPausedLabel = t("community_inbox.ai_paused");
  const hasUnread = chat.unread_messages_count > 0;

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!isPlainLeftClick(event)) return;
    event.preventDefault();
    selectAvatar({ avatarId, chatId: chat.id });
  };

  return (
    <div className="flex flex-col border-t border-secondary last:border-b hover:border-transparent [&:hover+div]:border-t-transparent">
      <Link
        href={buildAvatarPanelEntryUrl(chat.id, isFullscreen)}
        variant="custom"
        typography="body-sm"
        className="self-stretch"
        onClick={handleClick}
      >
        <span className="-mx-3 flex min-w-0 flex-1 items-center gap-3 rounded-2xl p-3 hover:bg-hover">
          <Avatar name={name} src={avatar_url} decorative />
          <span className="flex min-w-0 flex-1 items-center gap-2">
            <Typography
              variant="label-sm"
              component="span"
              className="min-w-0 flex-1"
              truncate
            >
              {chat.title ?? name}
            </Typography>
            {chat.ai_snoozed && (
              <Tooltip content={aiPausedLabel}>
                <Icon
                  name="clock-snooze"
                  size="sm"
                  color="tertiary"
                  aria-label={aiPausedLabel}
                />
              </Tooltip>
            )}
            {hasUnread && (
              <span className="flex size-4 shrink-0 items-center justify-center">
                <UnreadIndicator />
                <span className="sr-only">
                  {t("avatars.conversation_panel.unread")}
                </span>
              </span>
            )}
          </span>
        </span>
      </Link>
    </div>
  );
};
